import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import React, { KeyboardEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { appEnvironment } from '../../common/environment';
import {
  CONSENT_SECTIONS,
  getPrivacyNoticeURI,
} from '../../common/legalConstants';

interface SignUpConsentsProps {
  isSyd: boolean;
  isJa: boolean;
  termsURI: string;
  healthDataConsent: boolean;
  setHealthDataConsent: (val: boolean) => void;
  researchConsent: boolean;
  setResearchConsent: (val: boolean) => void;
  tcsAgreed: boolean;
  setTcsAgreed: (val: boolean) => void;
}

interface ConsentCardProps {
  checked: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const CHECKED_COLOR = '#8B7CFF';
const UNCHECKED_BORDER = '#E5E7EB';
const HOVER_BORDER = '#D1D5DB';

function ConsentCard({ checked, onToggle, children }: ConsentCardProps) {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <Box
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      sx={{
        width: '100%',
        minHeight: '44px',
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        boxSizing: 'border-box',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'flex-start',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        border: checked
          ? `2px solid ${CHECKED_COLOR}`
          : `1px solid ${UNCHECKED_BORDER}`,
        padding: checked ? '11px 15px' : '12px 16px',
        '&:hover': {
          borderColor: checked ? CHECKED_COLOR : HOVER_BORDER,
        },
        '&:focus-visible': {
          outline: 'none',
          boxShadow: `0 0 0 2px #F5F6F6, 0 0 0 4px ${CHECKED_COLOR}`,
        },
      }}
    >
      <Box
        sx={{
          width: '20px',
          height: '20px',
          minWidth: '20px',
          minHeight: '20px',
          borderRadius: '6px',
          marginTop: '1px',
          marginRight: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.15s ease',
          backgroundColor: checked ? CHECKED_COLOR : '#FFFFFF',
          border: checked
            ? `1.5px solid ${CHECKED_COLOR}`
            : '1.5px solid #D1D5DB',
        }}
      >
        {checked && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

export default function SignUpConsents({
  isSyd,
  isJa,
  termsURI,
  healthDataConsent,
  setHealthDataConsent,
  researchConsent,
  setResearchConsent,
  tcsAgreed,
  setTcsAgreed,
}: SignUpConsentsProps) {
  const { t } = useTranslation();
  const appName = appEnvironment.theme.appName;
  const { primary, mint, secondaryGrayText } = appEnvironment.theme.colours;

  // Resolved from legalConstants
  const healthDataNoticeURI = useMemo(
    () =>
      getPrivacyNoticeURI({
        isJa,
        language: i18next.language,
        section: CONSENT_SECTIONS.HEALTH_DATA,
      }),
    [isJa]
  );

  const researchNoticeURI = useMemo(
    () =>
      getPrivacyNoticeURI({
        isJa,
        language: i18next.language,
        section: CONSENT_SECTIONS.RESEARCH,
      }),
    [isJa]
  );

  if (!isSyd) {
    return (
      <Grid
        container
        size={{ xs: 12 }}
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <FormControlLabel
          label={
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              {!isJa && (
                <Typography variant="body1" sx={{ color: primary }}>
                  {t('iAgreeToAll')}&nbsp;
                </Typography>
              )}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={termsURI}
                className="underline"
                style={{ color: primary }}
              >
                {isJa
                  ? t('iAgreeToAllTermsAndConditions')
                  : t('termsAndConditions')}
              </a>
            </Box>
          }
          control={
            <Checkbox
              checked={tcsAgreed}
              onChange={(_e, checked) => setTcsAgreed(checked)}
              sx={{ color: primary, '&.Mui-checked': { color: mint } }}
            />
          }
        />
      </Grid>
    );
  }

  return (
    <Grid
      container
      size={{ xs: 12 }}
      direction="column"
      sx={{ mt: '24px', gap: '12px' }}
    >
      {/* Consent 1: Health data -> Section 4-1 */}
      <ConsentCard
        checked={healthDataConsent}
        onToggle={() => setHealthDataConsent(!healthDataConsent)}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: '13px',
            lineHeight: '20px',
            color: primary,
            wordBreak: 'break-word',
          }}
        >
          {(
            t('agreeHealthData', {
              appName,
              defaultValue: `I agree to ${appName} using my health data to provide and personalise my services.`,
            }) as string
          ).replace(/syd/gi, appName)}{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={healthDataNoticeURI}
            className="underline font-medium"
            style={{ color: primary }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {t('learnMore', 'Learn more')}
          </a>
        </Typography>
      </ConsentCard>

      {/* Consent 2: Research anonymisation -> Section 7-7 */}
      <ConsentCard
        checked={researchConsent}
        onToggle={() => setResearchConsent(!researchConsent)}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: '13px',
            lineHeight: '20px',
            color: primary,
            wordBreak: 'break-word',
          }}
        >
          {(
            t('agreeResearch', {
              appName,
              defaultValue: `I agree to ${appName} anonymising my health data for research, including with research partners.`,
            }) as string
          ).replace(/syd/gi, appName)}{' '}
          <span style={{ color: secondaryGrayText, marginRight: '6px' }}>
            ({t('optional', 'Optional')})
          </span>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={researchNoticeURI}
            className="underline font-medium"
            style={{ color: primary }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {t('learnMore', 'Learn more')}
          </a>
        </Typography>
      </ConsentCard>

      {/* Withdrawal note */}
      <Typography
        variant="body2"
        sx={{
          fontSize: '12px',
          color: '#9CA3AF',
          textAlign: 'center',
          width: '100%',
          mt: '8px',
          mb: '2px',
          userSelect: 'none',
        }}
      >
        {t('withdrawConsentAnytime', 'You can withdraw consent at any time.')}
      </Typography>
    </Grid>
  );
}
